import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FileText, MapPin, Package, Pencil, Plus, RotateCcw, Trash2, Truck, User, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useStore } from "../../context/StoreContext";
import { fmtDate, fmtTRY, ORDER_STATUS_COLORS } from "../../lib/format";

const RETURN_REASONS = ["Ürün hasarlı", "Yanlış ürün", "Ürün beklentimi karşılamadı", "Yanlış beden", "Eksik ürün", "Diğer"];
const CANCELABLE = ["Ödeme Bekliyor", "Ödeme Başarısız", "Taslak", "Ödeme Başarılı", "Hazırlanıyor", "Kargoya Hazır"];

const inputCls = "w-full h-11 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600";
const EMPTY_ADDR = { title: "", full_name: "", phone: "", city: "", district: "", address_line: "", postal_code: "", is_default: false };

export default function AccountPage() {
  const { customer, setCustomer } = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "siparisler";
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addrForm, setAddrForm] = useState(null);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [expanded, setExpanded] = useState(null);
  const [invoices, setInvoices] = useState({});
  const [details, setDetails] = useState({});
  const [returnForm, setReturnForm] = useState(null);

  const loadDetail = (orderNumber) => {
    api.get(`/orders/${orderNumber}`)
      .then(({ data }) => setDetails((s) => ({ ...s, [orderNumber]: data })))
      .catch(() => {});
  };

  const loadInvoice = (orderNumber) => {
    api.get(`/invoices/by-order/${orderNumber}`)
      .then(({ data }) => setInvoices((s) => ({ ...s, [orderNumber]: data })))
      .catch(() => {});
  };

  const cancelOrder = async (orderNumber) => {
    if (!window.confirm("Sipariş iptal edilsin mi?")) return;
    try {
      const { data } = await api.post(`/orders/${orderNumber}/cancel-request`);
      toast.success(data.result === "cancelled" ? "Siparişiniz iptal edildi" : "İptal talebiniz alındı");
      const { data: list } = await api.get("/orders/my");
      setOrders(list);
      loadDetail(orderNumber);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const submitReturn = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/orders/${returnForm.order_number}/returns`, {
        reason: returnForm.reason, description: returnForm.description || null,
      });
      toast.success("İade talebiniz alındı");
      setReturnForm(null);
      const { data: list } = await api.get("/orders/my");
      setOrders(list);
      loadDetail(returnForm.order_number);
    } catch (err) { toast.error(errMsg(err)); }
  };

  useEffect(() => {
    if (customer === false) navigate("/giris");
    if (customer) {
      setProfile({ name: customer.name, phone: customer.phone || "" });
      api.get("/orders/my").then(({ data }) => setOrders(data)).catch(() => {});
      api.get("/addresses").then(({ data }) => setAddresses(data)).catch(() => {});
    }
  }, [customer, navigate]);

  if (!customer) return <div className="py-24 text-center text-neutral-400 text-sm">Yükleniyor...</div>;

  const saveAddress = async (e) => {
    e.preventDefault();
    try {
      if (addrForm.address_id) {
        await api.put(`/addresses/${addrForm.address_id}`, addrForm);
      } else {
        await api.post("/addresses", addrForm);
      }
      const { data } = await api.get("/addresses");
      setAddresses(data);
      setAddrForm(null);
      toast.success("Adres kaydedildi");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/auth/me", profile);
      setCustomer(data);
      toast.success("Profil güncellendi");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.put("/auth/me/password", pwForm);
      setPwForm({ current_password: "", new_password: "" });
      toast.success("Şifreniz değiştirildi");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const TABS = [
    { id: "siparisler", label: "Siparişlerim", icon: Package },
    { id: "adresler", label: "Adreslerim", icon: MapPin },
    { id: "profil", label: "Profil", icon: User },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <Helmet><title>Hesabım | BIGELDİ</title></Helmet>
      <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight mb-2" data-testid="account-title">Merhaba, {customer.name}</h1>
      <p className="text-sm text-neutral-500 mb-8">{customer.email}</p>

      <div className="flex gap-2 mb-8 overflow-x-auto no-scrollbar" data-testid="account-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setParams({ tab: t.id })}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${tab === t.id ? "bg-[#121214] text-white" : "bg-white border border-[#E6E4DD] hover:border-amber-600"}`}
            data-testid={`account-tab-${t.id}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "siparisler" && (
        <div className="space-y-4" data-testid="account-orders">
          {orders.length === 0 ? (
            <div className="bg-white border border-[#E6E4DD] rounded-2xl p-12 text-center text-neutral-400 text-sm">
              Henüz siparişiniz yok. <Link to="/ara" className="text-amber-700 font-semibold underline">Alışverişe başlayın</Link>
            </div>
          ) : orders.map((o) => (
            <div key={o.order_id} className="bg-white border border-[#E6E4DD] rounded-2xl overflow-hidden" data-testid={`order-row-${o.order_number}`}>
              <button onClick={() => { const next = expanded === o.order_number ? null : o.order_number; setExpanded(next); if (next) { loadInvoice(o.order_number); loadDetail(o.order_number); } }} className="w-full flex flex-wrap items-center justify-between gap-3 p-5 text-left hover:bg-[#FAF9F5] transition-colors" data-testid={`order-expand-${o.order_number}`}>
                <div>
                  <p className="font-mono-ed font-bold">{o.order_number}</p>
                  <p className="text-xs text-neutral-400">{fmtDate(o.created_at)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${ORDER_STATUS_COLORS[o.status] || "bg-neutral-100"}`} data-testid="order-status-badge">{o.status}</span>
                  <span className="font-mono-ed font-bold">{fmtTRY(o.total)}</span>
                </div>
              </button>
              {expanded === o.order_number && (
                <div className="border-t border-[#E6E4DD] p-5 space-y-3 text-sm bg-[#FAF9F5]/60">
                  {o.items.map((i) => (
                    <div key={i.product_id + (i.variant_id || "")} className="flex items-center gap-3">
                      {i.image && <img src={i.image} alt="" className="w-12 h-14 rounded-lg object-cover" />}
                      <div className="flex-1">
                        <p className="font-medium">{i.name}</p>
                        <p className="text-[11px] text-neutral-400">{i.variant_label ? `${i.variant_label} · ` : ""}{i.sku} × {i.quantity}</p>
                      </div>
                      <p className="font-semibold">{fmtTRY(i.line_total)}</p>
                    </div>
                  ))}
                  <div className="text-xs text-neutral-500 pt-2 border-t border-[#E6E4DD] space-y-1">
                    {(() => {
                      const det = details[o.order_number];
                      const shipment = det?.shipment;
                      const returns = det?.returns || [];
                      return (
                        <>
                          {(o.cargo_company || o.cargo_tracking_number || shipment) && (
                            <div className="flex flex-wrap items-center justify-between gap-2 bg-white border border-[#E6E4DD] rounded-xl p-3 mb-2" data-testid="customer-shipment-box">
                              <p className="flex items-center gap-2 text-neutral-600">
                                <Truck size={14} className="text-amber-700" />
                                Kargo: <b>{o.cargo_company || shipment?.provider || "—"}</b>
                                {(o.cargo_tracking_number || shipment?.tracking_number) && <span className="font-mono-ed">· {o.cargo_tracking_number || shipment?.tracking_number}</span>}
                                <span className="text-[10px] font-bold text-teal-700">{o.shipping_status || ""}</span>
                              </p>
                              {shipment?.tracking_url && (
                                <a href={shipment.tracking_url} target="_blank" rel="noreferrer"
                                  className="text-[11px] font-semibold text-amber-700 underline" data-testid="customer-track-btn">
                                  Kargomu Takip Et
                                </a>
                              )}
                            </div>
                          )}
                          {det?.timeline?.length > 0 && (
                            <div className="bg-white border border-[#E6E4DD] rounded-xl p-3 mb-2" data-testid="customer-timeline">
                              <p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-2">Sipariş Geçmişi</p>
                              <div className="space-y-1.5">
                                {det.timeline.map((ev) => (
                                  <p key={ev.event_id} className="text-[11px] text-neutral-600">
                                    <span className="text-neutral-400">{fmtDate(ev.created_at)}</span> — {ev.description}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                          {returns.length > 0 && (
                            <p className="text-[11px] text-orange-700 mb-2" data-testid="customer-return-status">
                              İade talebi: <b>{returns[0].status === "REQUESTED" ? "İncelemede" : returns[0].status === "APPROVED" ? "Onaylandı" : returns[0].status === "COMPLETED" ? "Tamamlandı" : returns[0].status === "REJECTED" ? "Reddedildi" : returns[0].status}</b> ({returns[0].reason})
                            </p>
                          )}
                          <div className="flex gap-2 pt-1">
                            {CANCELABLE.includes(o.status) && (
                              <button onClick={() => cancelOrder(o.order_number)}
                                className="h-9 px-4 rounded-full border border-rose-200 text-rose-600 text-xs font-semibold hover:bg-rose-50 flex items-center gap-1.5"
                                data-testid={`order-cancel-${o.order_number}`}>
                                <XCircle size={13} /> İptal Et
                              </button>
                            )}
                            {o.status === "Teslim Edildi" && returns.length === 0 && (
                              <button onClick={() => setReturnForm({ order_number: o.order_number, reason: RETURN_REASONS[0], description: "" })}
                                className="h-9 px-4 rounded-full border border-amber-300 text-amber-700 text-xs font-semibold hover:bg-amber-50 flex items-center gap-1.5"
                                data-testid={`order-return-${o.order_number}`}>
                                <RotateCcw size={13} /> İade Talebi Oluştur
                              </button>
                            )}
                          </div>
                        </>
                      );
                    })()}
                    <p>Teslimat: {o.shipping_address.full_name} — {o.shipping_address.address_line}, {o.shipping_address.district}/{o.shipping_address.city}</p>
                    <p>Ara toplam {fmtTRY(o.subtotal)} · Kargo {o.shipping_fee === 0 ? "Ücretsiz" : fmtTRY(o.shipping_fee)}
                      {o.campaign_discount > 0 && ` · Kampanya -${fmtTRY(o.campaign_discount)}`}
                      {o.coupon_discount > 0 && ` · Kupon -${fmtTRY(o.coupon_discount)}`}
                      {` · KDV dahil ${fmtTRY(o.vat_total)}`}</p>
                    <p>Ödeme: <b>{o.payment_status}</b>{o.payment_transaction_id && ` · ${o.payment_transaction_id}`}</p>
                    {(() => {
                      const inv = invoices[o.order_number];
                      if (inv === undefined) return <p className="text-neutral-400">Fatura bilgisi yükleniyor...</p>;
                      if (!inv) return null;
                      const INV_LABEL = { DRAFT: "Hazırlanıyor", PENDING: "Hazırlanıyor", SENT: "Gönderildi", ACCEPTED: "Onaylandı", REJECTED: "Hata oluştu", FAILED: "Hata oluştu", CANCELLED: "İptal Edildi" };
                      return (
                        <div className="flex items-center justify-between bg-white border border-[#E6E4DD] rounded-xl p-3 mt-2" data-testid="customer-invoice-box">
                          <p className="flex items-center gap-2 text-neutral-600">
                            <FileText size={14} className="text-amber-700" />
                            Fatura: <b>{inv.invoice_number || "—"}</b>
                            <span className="text-[10px] text-neutral-400">({inv.invoice_type === "E_INVOICE" ? "e-Fatura" : inv.invoice_type === "E_ARCHIVE" ? "e-Arşiv" : "Kontrol bekliyor"})</span>
                            <span className={`text-[10px] font-bold ${["SENT", "ACCEPTED"].includes(inv.status) ? "text-emerald-700" : "text-amber-700"}`}>{INV_LABEL[inv.status] || inv.status}</span>
                          </p>
                          {inv.invoice_pdf_url && ["SENT", "ACCEPTED"].includes(inv.status) && (
                            <a href={inv.invoice_pdf_url} target="_blank" rel="noreferrer" className="text-[11px] font-semibold text-amber-700 underline" data-testid="customer-invoice-view">
                              Faturayı Görüntüle
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "adresler" && (
        <div data-testid="account-addresses">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            {addresses.map((a) => (
              <div key={a.address_id} className="bg-white border border-[#E6E4DD] rounded-2xl p-5 relative" data-testid={`address-card-${a.address_id}`}>
                {a.is_default && <span className="absolute top-4 right-4 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Varsayılan</span>}
                <p className="font-bold text-sm">{a.title || "Adresim"}</p>
                <p className="text-sm mt-1">{a.full_name}</p>
                <p className="text-xs text-neutral-500 mt-1">{a.address_line} {a.district}/{a.city}</p>
                <p className="text-xs text-neutral-500">{a.phone}</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setAddrForm({ ...EMPTY_ADDR, ...a })} className="text-xs font-semibold text-amber-700 flex items-center gap-1" data-testid="address-edit"><Pencil size={12} /> Düzenle</button>
                  <button onClick={async () => { await api.delete(`/addresses/${a.address_id}`); setAddresses(addresses.filter((x) => x.address_id !== a.address_id)); }}
                    className="text-xs font-semibold text-rose-600 flex items-center gap-1" data-testid="address-delete"><Trash2 size={12} /> Sil</button>
                </div>
              </div>
            ))}
            <button onClick={() => setAddrForm({ ...EMPTY_ADDR, full_name: customer.name, phone: customer.phone || "" })}
              className="border-2 border-dashed border-[#E6E4DD] rounded-2xl p-5 text-sm font-semibold text-neutral-500 hover:border-amber-600 hover:text-amber-700 transition-colors flex items-center justify-center gap-2 min-h-[140px]"
              data-testid="address-add">
              <Plus size={16} /> Yeni Adres Ekle
            </button>
          </div>
          {addrForm && (
            <form onSubmit={saveAddress} className="bg-white border border-[#E6E4DD] rounded-2xl p-6 space-y-3" data-testid="address-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input placeholder="Adres başlığı (Ev, İş...)" value={addrForm.title || ""} onChange={(e) => setAddrForm({ ...addrForm, title: e.target.value })} className={inputCls} data-testid="address-title" />
                <input required placeholder="Ad Soyad *" value={addrForm.full_name} onChange={(e) => setAddrForm({ ...addrForm, full_name: e.target.value })} className={inputCls} data-testid="address-fullname" />
                <input required placeholder="Telefon *" value={addrForm.phone} onChange={(e) => setAddrForm({ ...addrForm, phone: e.target.value })} className={inputCls} data-testid="address-phone" />
                <input required placeholder="İl *" value={addrForm.city} onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} className={inputCls} data-testid="address-city" />
                <input placeholder="İlçe" value={addrForm.district || ""} onChange={(e) => setAddrForm({ ...addrForm, district: e.target.value })} className={inputCls} data-testid="address-district" />
                <input placeholder="Posta kodu" value={addrForm.postal_code || ""} onChange={(e) => setAddrForm({ ...addrForm, postal_code: e.target.value })} className={inputCls} />
              </div>
              <textarea required placeholder="Açık adres *" rows={2} value={addrForm.address_line} onChange={(e) => setAddrForm({ ...addrForm, address_line: e.target.value })} className={`${inputCls} py-3 resize-none`} data-testid="address-line" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={addrForm.is_default} onChange={(e) => setAddrForm({ ...addrForm, is_default: e.target.checked })} className="accent-amber-600 w-4 h-4" data-testid="address-default" /> Varsayılan adresim yap</label>
              <div className="flex gap-2">
                <button type="submit" className="px-6 h-11 rounded-full bg-[#121214] text-white text-sm font-semibold hover:bg-amber-600 transition-colors" data-testid="address-save">Kaydet</button>
                <button type="button" onClick={() => setAddrForm(null)} className="px-6 h-11 rounded-full border border-[#E6E4DD] text-sm" data-testid="address-cancel">Vazgeç</button>
              </div>
            </form>
          )}
        </div>
      )}

      {tab === "profil" && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="account-profile">
          <form onSubmit={saveProfile} className="bg-white border border-[#E6E4DD] rounded-2xl p-6 space-y-3">
            <h2 className="font-display font-bold">Profil Bilgileri</h2>
            <input placeholder="Ad Soyad" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputCls} data-testid="profile-name" />
            <input placeholder="Telefon" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inputCls} data-testid="profile-phone" />
            <button type="submit" className="px-6 h-11 rounded-full bg-[#121214] text-white text-sm font-semibold hover:bg-amber-600 transition-colors" data-testid="profile-save">Kaydet</button>
          </form>
          <form onSubmit={changePassword} className="bg-white border border-[#E6E4DD] rounded-2xl p-6 space-y-3">
            <h2 className="font-display font-bold">Şifre Değiştir</h2>
            {customer.auth_provider === "google" ? (
              <p className="text-xs text-neutral-500">Google hesabıyla giriş yapıyorsunuz. Şifre belirlerseniz e-posta ile de giriş yapabilirsiniz.</p>
            ) : (
              <input type="password" required placeholder="Mevcut şifre" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className={inputCls} data-testid="password-current" />
            )}
            <input type="password" required minLength={6} placeholder="Yeni şifre" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className={inputCls} data-testid="password-new" />
            <button type="submit" className="px-6 h-11 rounded-full bg-[#121214] text-white text-sm font-semibold hover:bg-amber-600 transition-colors" data-testid="password-save">Şifremi Değiştir</button>
          </form>
        </div>
      )}
      {returnForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="return-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setReturnForm(null)} />
          <form onSubmit={submitReturn} className="relative bg-white rounded-3xl p-7 w-full max-w-md space-y-4" data-testid="return-form">
            <h2 className="font-display font-extrabold text-xl">İade Talebi</h2>
            <p className="text-xs text-neutral-500 font-mono-ed">{returnForm.order_number}</p>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">İade Nedeni *</label>
              <select value={returnForm.reason} onChange={(e) => setReturnForm({ ...returnForm, reason: e.target.value })}
                className={inputCls} data-testid="return-reason">
                {RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1.5">
                Açıklama {returnForm.reason === "Diğer" ? "*" : "(isteğe bağlı)"}
              </label>
              <textarea rows={3} value={returnForm.description} required={returnForm.reason === "Diğer"}
                onChange={(e) => setReturnForm({ ...returnForm, description: e.target.value })}
                className={`${inputCls} py-3 resize-none`} data-testid="return-description" />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-6 h-11 rounded-full bg-[#121214] text-white text-sm font-semibold hover:bg-amber-600 transition-colors" data-testid="return-submit">Talebi Gönder</button>
              <button type="button" onClick={() => setReturnForm(null)} className="px-6 h-11 rounded-full border border-[#E6E4DD] text-sm">Vazgeç</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
